import { compact } from 'lodash-es'
import {
  OrderEstimate,
  OrderHistoryReceipt,
  OvenCookHistoryItem,
  ReviewSummary,
  SkippedWeeks,
  TermStatus,
  TermStatusSubscriptionType,
  UserV1,
} from '@tovala/browser-apis-combinedapi'

import {
  addToDate,
  isDateAfter,
  isDateBefore,
  isDatePast,
  parseToDate,
} from './dates'
import { TermToRate, UserTerm } from 'types/internal'
import { DateTime } from 'luxon'

// Since we don't have live delivery tracking, we need to add a buffer to the delivery date to avoid showing "Arrives" too soon
const DELIVERY_BUFFER_DAYS = 2

export function getDeliveryStatus({
  receipt,
}: {
  receipt: OrderHistoryReceipt
}) {
  if (
    receipt.orderStatus === 'complete' ||
    receipt.orderStatus === 'refunded'
  ) {
    if (!receipt.trackingURL) {
      return 'Arrives'
    }

    const dateWithBuffer = addToDate(parseToDate(receipt.deliveryDate), {
      quantity: DELIVERY_BUFFER_DAYS,
      units: 'days',
    })
    return isDatePast(dateWithBuffer) ? 'Arrived' : 'Arrives'
  } else if (receipt.orderStatus === 'on_break') {
    return 'Paused'
  } else if (receipt.orderStatus === 'skipped') {
    return 'Skipped'
  } else if (receipt.orderStatus === 'canceled') {
    return 'Canceled'
  } else if (receipt.orderStatus === 'processing') {
    return 'Processing'
  } else if (receipt.orderStatus === 'payment_error') {
    return 'Payment Error'
  }
}

export function getMostRecentTermMealsToRate({
  ignoreDeliveryWindow = false,
  termsToRate,
}: {
  ignoreDeliveryWindow?: boolean
  termsToRate: TermToRate[]
}) {
  const today = new Date()

  let termToRate = termsToRate.find((term) => {
    if (ignoreDeliveryWindow) {
      return term.meals.length > 0
    }

    /*
     * Find the most recent past term that has meals to rate that was delivered
     * at least 5 days ago, but isn't too far past the delivery date. These meals
     * are likely to have been eaten, but no so long ago the user has forgotten how
     * they feel about them
     */
    const deliveryDate = parseToDate(term.deliveryDate)
    const averageTimeToConsumeMeals = addToDate(deliveryDate, {
      quantity: 5,
      units: 'days',
    })
    const tooOldToPromptToRate = addToDate(deliveryDate, {
      quantity: 12,
      units: 'days',
    })

    return (
      isDateAfter(today, averageTimeToConsumeMeals) &&
      isDateBefore(today, tooOldToPromptToRate)
    )
  })

  if (termToRate) {
    /*
     * Check for more recent terms than the one found above that have cooked meals and add those
     * into the ratings slider
     */
    const moreRecentTerms = termsToRate.filter((term) =>
      termToRate ? term.termID > termToRate.termID : false,
    )
    if (moreRecentTerms) {
      // Create a clone so we can add more recent cooked meals
      termToRate = Object.assign({}, termToRate)

      moreRecentTerms.forEach((term) => {
        if (!termToRate) {
          return
        }

        const cookedMeals = term.meals.filter((meal) => meal.cooked)
        termToRate.meals = [...termToRate.meals, ...cookedMeals]

        const cooked = termToRate.meals
          .filter((meal) => meal.cooked)
          .sort((a, b) =>
            a.cooked && b.cooked
              ? +new Date(b.cooked.end_time) - +new Date(a.cooked.end_time)
              : -1,
          )
        const notCooked = termToRate.meals.filter((meal) => !meal.cooked)

        termToRate.meals = [...cooked, ...notCooked]
      })
    }
  }

  return termToRate
}

/*
 * Returns a formatted date string for the order cutoff time in CT.
 * Note that we need to handle the DST offsets because the backend team adjusts the
 * order by dates manually when DST starts/ends (for example, when DST ends, the
 * backend team adds an hour to the future term order by dates).
 */
export function getOrderCutoffFormattedDate({
  format = 'full',
  orderByDate,
}: {
  format?: 'full' | 'short'
  orderByDate: string
}) {
  const isDST = DateTime.now().setZone('US/Central').isInDST

  const orderByDateCT = getOrderByDateCT({ orderByDate, isDST })

  const meridiem = orderByDateCT.toFormat('a').toLowerCase()
  if (format === 'full') {
    return `${orderByDateCT.toFormat("EEEE, M/d 'at' h")}${meridiem} CT`
  }

  return `${orderByDateCT.toFormat("EEE 'at' h")}${meridiem} CT`
}

export function getOrderByDateCT({
  orderByDate,
  isDST,
}: {
  orderByDate: string
  isDST: boolean
}) {
  const offset = isDST ? 'UTC-5' : 'UTC-6'
  return DateTime.fromISO(orderByDate, { zone: offset })
}

export function getOrderSummaryDetails({
  accountBalanceCents,
  isLegacyFreeShipping,
  orderEstimate,
  selectedSubscription,
}: {
  accountBalanceCents: number
  isLegacyFreeShipping: boolean
  orderEstimate: OrderEstimate | undefined
  selectedSubscription: TermStatusSubscriptionType | null
}) {
  if (selectedSubscription === null || orderEstimate === undefined) {
    return undefined
  }

  const {
    estimatedShipping,
    estimatedSubtotal,
    estimatedTotal,
    estimatedItemDiscount,
    extrasTotal,
    mealsSurchargesTotal,
    mealsTotal,
    subscriptionMealPriceCents,
  } = orderEstimate

  let isFreeShipping =
    (estimatedShipping.discountType === 'cents' &&
      estimatedShipping.discountAmount === estimatedShipping.shippingCents) ||
    (estimatedShipping.discountType === 'percent' &&
      estimatedShipping.discountAmount === 100)

  let estimatedSubtotalCents = estimatedSubtotal - accountBalanceCents
  let estimatedDiscountCents = estimatedItemDiscount + accountBalanceCents

  // If the user is grandfathered in, the shipping is free,
  // so we need to subtract the shipping from the subtotal
  if (!isFreeShipping && isLegacyFreeShipping) {
    isFreeShipping = true
    estimatedSubtotalCents =
      estimatedSubtotalCents - estimatedShipping.shippingCents
  }

  if (estimatedSubtotalCents < 0) {
    estimatedSubtotalCents = 0
  }

  if (estimatedSubtotalCents === 0) {
    // Don't show a discount greater than the total
    estimatedDiscountCents = estimatedTotal

    if (isFreeShipping) {
      // If the shipping is free we need to subtract the shipping,
      // since we're showing the shipping as free separately and the total includes shipping
      estimatedDiscountCents =
        estimatedDiscountCents - estimatedShipping.shippingCents
    }
  }

  return {
    additionalCostCents: extrasTotal + mealsSurchargesTotal,
    baseMealCents: subscriptionMealPriceCents,
    estimatedDiscountCents,
    estimatedSubtotalCents,
    estimatedTotalCents: estimatedTotal,
    isDiscounted: estimatedSubtotalCents < estimatedTotal,
    isFreeShipping,
    isMultipleDiscounts: estimatedItemDiscount > 0 && accountBalanceCents > 0,
    mealPlanCents: mealsTotal,
    shippingCents: estimatedShipping.shippingCents,
  } as const
}

export function isRateableReceipt(receipt: OrderHistoryReceipt) {
  return (
    receipt.orderStatus === 'complete' || receipt.orderStatus === 'refunded'
  )
}

export function getMostRecentCookByMealID(cookHistory: OvenCookHistoryItem[]) {
  const mostRecentCookByMealID = new Map<number, OvenCookHistoryItem>()

  cookHistory.forEach((cook) => {
    if (!cook.meal_id) {
      return
    }

    const mealID = Number.parseInt(cook.meal_id, 10)
    const mostRecentCook = mostRecentCookByMealID.get(mealID)

    if (
      !mostRecentCook ||
      new Date(cook.end_time).getTime() >
        new Date(mostRecentCook.end_time).getTime()
    ) {
      mostRecentCookByMealID.set(mealID, cook)
    }
  })

  return mostRecentCookByMealID
}

export function getTermsToRate({
  cookHistory,
  includeReviewedMeals = false,
  mealReviewSummaries,
  orderHistoryReceipts,
}: {
  cookHistory: OvenCookHistoryItem[] | ''
  includeReviewedMeals?: boolean
  mealReviewSummaries: ReviewSummary | undefined
  orderHistoryReceipts: OrderHistoryReceipt[]
}) {
  const mostRecentCookByMealID = getMostRecentCookByMealID(cookHistory || [])

  let reviewedMealIDs: number[] = []

  if (mealReviewSummaries && !includeReviewedMeals) {
    reviewedMealIDs = mealReviewSummaries.reviews.map((review) => review.mealId)
  }

  const termsToRate: TermToRate[] = []

  if (orderHistoryReceipts) {
    orderHistoryReceipts.forEach((receipt) => {
      if (isRateableReceipt(receipt) && receipt.selectedMeals) {
        const uniqueMeals = new Set(
          receipt.selectedMeals.map((meal) => meal.id),
        )
        const uniqueMealsArr = compact(
          [...Array.from(uniqueMeals)].map((mealID) =>
            receipt.selectedMeals.find((meal) => meal.id === mealID),
          ),
        )
        const meals = uniqueMealsArr
          .filter((meal) => !reviewedMealIDs.includes(meal.id))
          .map((meal) => {
            return Object.assign({}, meal, {
              cooked: mostRecentCookByMealID.get(meal.id),
            })
          })

        const cooked = meals
          .filter((meal) => meal.cooked)
          .sort((a, b) =>
            a.cooked && b.cooked
              ? +new Date(b.cooked.end_time) - +new Date(a.cooked.end_time)
              : -1,
          )
        const notCooked = meals.filter((meal) => !meal.cooked)

        const term: TermToRate = {
          deliveryDate: receipt.deliveryDate,
          meals: [...cooked, ...notCooked],
          termID: receipt.termID,
        }
        termsToRate.push(term)
      }
    })
  }

  return termsToRate
}

export interface TermStatusDefaultsUpdate {
  subTermID: string
  termStatus: TermStatus
}

export function isTermDelivering(
  termStatus: Pick<TermStatus, 'isOnBreak' | 'isSkipped' | 'isStaticSkipped'>,
) {
  return (
    !termStatus.isSkipped &&
    !termStatus.isStaticSkipped &&
    !termStatus.isOnBreak
  )
}

export function getTermStatusUpdatesForNewDefaults({
  onlyWeeksWithMaxSelections,
  shipPeriod,
  subscriptionTypeID,
  termStatuses,
}: {
  onlyWeeksWithMaxSelections?: number
  shipPeriod: number
  subscriptionTypeID: string
  termStatuses: TermStatus[]
}): TermStatusDefaultsUpdate[] {
  const termStatusesInScope =
    onlyWeeksWithMaxSelections === undefined
      ? termStatuses
      : termStatuses.filter(
          (termStatus) =>
            termStatus.subscriptionType?.maxSelections ===
            onlyWeeksWithMaxSelections,
        )

  return termStatusesInScope.filter(isTermDelivering).flatMap((termStatus) => {
    const selectedShipPeriod = termStatus.subTerms.find(
      (subTerm) => subTerm.subTermID === termStatus.selectedSubTermID,
    )?.shipPeriod

    const isSubscriptionTypeDifferent =
      !!termStatus.subscriptionType &&
      termStatus.subscriptionType.id !== subscriptionTypeID
    const isShipPeriodDifferent =
      !!selectedShipPeriod && selectedShipPeriod !== shipPeriod

    const availableSubTermForShipPeriod = isShipPeriodDifferent
      ? termStatus.subTerms.find(
          (subTerm) => subTerm.shipPeriod === shipPeriod && subTerm.isAvailable,
        )
      : undefined

    if (!isSubscriptionTypeDifferent && !availableSubTermForShipPeriod) {
      return []
    }

    return [
      {
        subTermID:
          availableSubTermForShipPeriod?.subTermID ??
          termStatus.selectedSubTermID,
        termStatus,
      },
    ]
  })
}

export function getUpgradedSubscriptionTermID({
  termStatuses,
  user,
}: {
  termStatuses: TermStatus[]
  user: UserV1
}) {
  const subscriptionMaxSelections =
    user.subscription.subscriptionType?.maxSelections

  const upgradedTerm =
    subscriptionMaxSelections &&
    termStatuses.find((term) => {
      return (
        term.subscriptionType &&
        term.subscriptionType.maxSelections > subscriptionMaxSelections
      )
    })

  return upgradedTerm ? upgradedTerm.termID : null
}

export function getUserTerm({
  orderEstimate,
  skippedWeeks,
  term,
  user,
}: {
  orderEstimate?: OrderEstimate
  skippedWeeks: SkippedWeeks | undefined
  term: TermStatus
  user: UserV1
}) {
  const selectedSubTerm = term.subTerms.find(
    (subTerm) => subTerm.subTermID === term.selectedSubTermID,
  )

  const { mealSelections: termMealSelections, ...restTerm } = term
  const maxSelections =
    orderEstimate?.mealSubscriptionCount ?? term.subscriptionType?.maxSelections
  const mealSelections = orderEstimate?.mealSelections ?? termMealSelections

  const userTerm: UserTerm = {
    ...restTerm,
    hasSelectedAllMeals: maxSelections === mealSelections.length,
    isSkipped: skippedWeeks
      ? skippedWeeks.termids.includes(term.termID)
      : term.isSkipped,
    isStaticSkipped: skippedWeeks
      ? skippedWeeks.staticTermIDs.includes(term.termID)
      : term.isStaticSkipped,
    mealSelections,
    mealSwaps: selectedSubTerm?.mainMenu?.mealSwaps
      ? [...selectedSubTerm.mainMenu.mealSwaps]
      : [],
    selectedSubscriptionTypeIsDefault:
      term.subscriptionType && user?.subscription.subscriptionType
        ? term.subscriptionType.maxSelections ===
          user.subscription.subscriptionType.maxSelections
        : true,
    selectedSubTerm,
    subTerms: [...term.subTerms]
      .filter((subTerm) => subTerm.isAvailable)
      .sort((a, b) => a.shipPeriod - b.shipPeriod),
  }

  return userTerm
}
